import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';

// ── Types ─────────────────────────────────────────────────────────

interface BrokerContact {
  name: string;
  email: string;
}

interface ContractYear {
  year_label: string;
  contract_number: string;
  effective_date: string;
  expiry_date: string;
  tab_label: string;
  all_closed: boolean;
}

interface CoverholderConfig {
  coverholder_id: string;
  coverholder_name: string;
  broker_name: string;
  broker_contacts: BrokerContact[];
  client_code: string;
  contract_years: ContractYear[];
  risk_code: string;
  deductible_amount: number;
  tcm_fee_reserve_default: number;
  coro_split: boolean;
  billing_cadence: string;
  active: boolean;
  s3_workbook_prefix: string;
  send_enabled: boolean;
  from_email: string;
}

interface QueueReport {
  report_id: string;
  coverholder_id: string;
  coverholder_name: string;
  billing_period: string;
  generated_at: string;
  generated_by: string;
  status: 'pending' | 'approved' | 'sent' | 'failed';
  fee_total: number;
  claim_count: number;
  stub_warnings: string[];
  workbook_s3_key: string;
  coro_workbook_s3_key: string | null;
  invoice_s3_keys: string[];
  approved_by: string | null;
  approved_at: string | null;
  sent_at: string | null;
  send_error: string | null;
}

type View = 'queue' | 'detail' | 'config' | 'config-edit' | 'generate';

// ── Feature Flags (read from env at build time) ───────────────────

const BDX_SEND_ENABLED = process.env.REACT_APP_BDX_SEND_ENABLED === 'true';
const TCM_FEES_ENABLED = process.env.REACT_APP_TCM_FEES_ENABLED === 'true';

// ── Badges ────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    sent: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  const labels: Record<string, string> = {
    pending: 'Pending',
    approved: 'Approved',
    sent: 'Sent',
    failed: 'Failed',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-600'}`}>
      {labels[status] || status}
    </span>
  );
}

function StubBadge({ warnings }: { warnings: string[] }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700" title={warnings.join(', ')}>
      Stubs Active ({warnings.length})
    </span>
  );
}

// ── Helpers ───────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
}

function getCurrentPeriod(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
}

// ── Main Component ────────────────────────────────────────────────

export default function BdxCoverholder() {
  const [view, setView] = useState<View>('queue');
  const [configs, setConfigs] = useState<CoverholderConfig[]>([]);
  const [queue, setQueue] = useState<QueueReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<QueueReport | null>(null);
  const [editingConfig, setEditingConfig] = useState<CoverholderConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [queueFilter, setQueueFilter] = useState('all');

  // Generate form state
  const [generateCoverholderId, setGenerateCoverholderId] = useState('');
  const [generatePeriod, setGeneratePeriod] = useState(getCurrentPeriod());
  const [generating, setGenerating] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const data = await api.getBdxCoverholderConfigs();
      setConfigs(data.configs || []);
    } catch (err: any) { setError(err.message); }
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      const params: Record<string, string> = {};
      if (queueFilter !== 'all') params.status = queueFilter;
      const data = await api.getBdxQueue(params);
      setQueue(data.reports || []);
    } catch (err: any) { setError(err.message); }
  }, [queueFilter]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);
  useEffect(() => { if (view === 'queue') fetchQueue(); }, [view, fetchQueue]);

  const handleGenerate = async () => {
    if (!generateCoverholderId || !generatePeriod) {
      setError('Coverholder and billing period are required');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      await api.generateBdxCoverholder({
        coverholder_id: generateCoverholderId,
        billing_period: generatePeriod,
      });
      await fetchQueue();
      setView('queue');
    } catch (err: any) { setError(err.message); }
    finally { setGenerating(false); }
  };

  const handleApprove = async (reportId: string) => {
    if (!BDX_SEND_ENABLED) {
      setError('BDX email sending is disabled (BDX_SEND_ENABLED=false). Contact Richard to enable.');
      return;
    }
    if (!window.confirm('Approve and send this bordereaux report?')) return;
    setLoading(true);
    setError('');
    try {
      await api.approveBdxReport(reportId, { approved_by: 'portal_user' });
      await fetchQueue();
      if (selectedReport?.report_id === reportId) {
        const updated = await api.getBdxReport(reportId);
        setSelectedReport(updated);
      }
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleDownload = async (reportId: string, type?: string) => {
    try {
      const data = await api.getBdxDownload(reportId, type);
      if (data.download_url) {
        const a = document.createElement('a');
        a.href = data.download_url;
        a.download = data.filename || 'download';
        a.click();
      }
    } catch (err: any) { setError(err.message); }
  };

  const openReport = (report: QueueReport) => {
    setSelectedReport(report);
    setView('detail');
  };

  const startEditConfig = (config?: CoverholderConfig) => {
    setEditingConfig(config || {
      coverholder_id: '', coverholder_name: '', broker_name: '',
      broker_contacts: [{ name: '', email: '' }],
      client_code: '', contract_years: [],
      risk_code: 'GN - Nursing Homes/Long-Term & Allied',
      deductible_amount: 0, tcm_fee_reserve_default: 3000,
      coro_split: false, billing_cadence: 'monthly',
      active: true, s3_workbook_prefix: '',
      send_enabled: false, from_email: 'billing@trans11claims.com',
    });
    setView('config-edit');
  };

  const handleSaveConfig = async (config: CoverholderConfig) => {
    setLoading(true);
    setError('');
    try {
      await api.saveBdxCoverholderConfig(config);
      await fetchConfigs();
      setView('config');
      setEditingConfig(null);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  // Queue filtering
  const filteredQueue = queue.filter(r => {
    if (queueFilter === 'all') return true;
    return r.status === queueFilter;
  });

  const countFor = (filter: string) => queue.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  }).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Coverholder Bordereaux</h1>
          <p className="text-sm text-slate-500 mt-0.5">Billing bordereaux &amp; TCM fee invoices</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setView('queue')}
            className={`px-3 py-1.5 text-sm rounded ${view === 'queue' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Report Queue
          </button>
          <button
            onClick={() => setView('config')}
            className={`px-3 py-1.5 text-sm rounded ${view === 'config' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          >
            Coverholders
          </button>
          <button
            onClick={() => setView('generate')}
            className="px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700"
          >
            + Generate Now
          </button>
        </div>
      </div>

      {/* Feature flag banners */}
      {!TCM_FEES_ENABLED && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-amber-700 text-sm">
          TCM fee data is pending T&amp;E table confirmation. Fee columns will show $0.00 until enabled.
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 ml-2">x</button>
        </div>
      )}

      {/* ── Queue View ── */}
      {view === 'queue' && (
        <div>
          <div className="flex items-center gap-1 mb-4">
            {(['all', 'pending', 'approved', 'sent', 'failed'] as const).map(f => (
              <button
                key={f}
                onClick={() => setQueueFilter(f)}
                className={`px-3 py-1.5 text-xs rounded-full font-medium ${
                  queueFilter === f ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                <span className="ml-1 opacity-70">{countFor(f)}</span>
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Coverholder</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Period</th>
                  <th className="text-left px-4 py-2 font-medium text-slate-600">Generated</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Claims</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Fee Total</th>
                  <th className="text-center px-4 py-2 font-medium text-slate-600">Status</th>
                  <th className="text-center px-4 py-2 font-medium text-slate-600">Warnings</th>
                  <th className="text-right px-4 py-2 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueue.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                      No reports in queue. Click "Generate Now" to create one.
                    </td>
                  </tr>
                ) : filteredQueue.map(r => (
                  <tr key={r.report_id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer"
                      onClick={() => openReport(r)}>
                    <td className="px-4 py-2 font-medium text-slate-800">{r.coverholder_name}</td>
                    <td className="px-4 py-2 text-slate-600">{r.billing_period}</td>
                    <td className="px-4 py-2 text-slate-500 text-xs">{formatDate(r.generated_at)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.claim_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{formatCurrency(r.fee_total)}</td>
                    <td className="px-4 py-2 text-center"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-2 text-center"><StubBadge warnings={r.stub_warnings} /></td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownload(r.report_id); }}
                        className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Report Detail View ── */}
      {view === 'detail' && selectedReport && (
        <ReportDetail
          report={selectedReport}
          onBack={() => { setView('queue'); setSelectedReport(null); }}
          onApprove={handleApprove}
          onDownload={handleDownload}
          sendEnabled={BDX_SEND_ENABLED}
        />
      )}

      {/* ── Generate View ── */}
      {view === 'generate' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-lg">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Generate Bordereaux</h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Coverholder</label>
              <select
                value={generateCoverholderId}
                onChange={(e) => setGenerateCoverholderId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              >
                <option value="">Select coverholder...</option>
                {configs.filter(c => c.active).map(c => (
                  <option key={c.coverholder_id} value={c.coverholder_id}>
                    {c.coverholder_name} ({c.broker_name})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Billing Period</label>
              <input
                type="month"
                value={generatePeriod}
                onChange={(e) => setGeneratePeriod(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <button
                onClick={handleGenerate}
                disabled={generating || !generateCoverholderId}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
              >
                {generating ? 'Generating...' : 'Generate'}
              </button>
              <button
                onClick={() => setView('queue')}
                className="px-4 py-2 text-sm rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Config List View ── */}
      {view === 'config' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-slate-800">Coverholder Configurations</h2>
            <button
              onClick={() => startEditConfig()}
              className="px-3 py-1.5 text-sm rounded bg-green-600 text-white hover:bg-green-700"
            >
              + Add Coverholder
            </button>
          </div>

          {configs.length === 0 ? (
            <div className="bg-white rounded-lg shadow border border-slate-200 p-8 text-center text-slate-400">
              <LoadingSpinner message="Loading configurations..." />
            </div>
          ) : (
            <div className="space-y-4">
              {configs.map(c => (
                <div key={c.coverholder_id} className="bg-white rounded-lg shadow border border-slate-200 p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-800">{c.coverholder_name}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Broker: {c.broker_name} &middot; Client: {c.client_code} &middot;
                        {c.contract_years.length} contract year{c.contract_years.length !== 1 ? 's' : ''}
                      </p>
                      <div className="flex gap-2 mt-2">
                        {c.active
                          ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Active</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Inactive</span>}
                        {c.coro_split && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">CORO Split</span>
                        )}
                        {c.send_enabled
                          ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Send Enabled</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Send Disabled</span>}
                      </div>
                    </div>
                    <button
                      onClick={() => startEditConfig(c)}
                      className="px-3 py-1 text-xs rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
                    >
                      Edit
                    </button>
                  </div>

                  {/* Contract years grid */}
                  <div className="mt-4">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Contract Years</p>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                      {c.contract_years.map(cy => (
                        <div key={cy.contract_number}
                          className="rounded bg-slate-50 border border-slate-100 px-3 py-2 text-xs">
                          <p className="font-medium text-slate-700">{cy.tab_label}</p>
                          <p className="text-slate-500">{cy.contract_number}</p>
                          <p className="text-slate-400">{cy.effective_date} — {cy.expiry_date}</p>
                          {cy.all_closed && (
                            <span className="text-amber-600 font-medium">All Claims Closed</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Broker contacts */}
                  <div className="mt-4">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Broker Contacts</p>
                    <div className="flex flex-wrap gap-2">
                      {c.broker_contacts.map((bc, i) => (
                        <span key={i} className="text-xs bg-slate-50 border border-slate-100 rounded px-2 py-1">
                          {bc.name} &lt;{bc.email}&gt;
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Config Edit View ── */}
      {view === 'config-edit' && editingConfig && (
        <ConfigEditor
          config={editingConfig}
          onSave={handleSaveConfig}
          onCancel={() => { setView('config'); setEditingConfig(null); }}
          loading={loading}
        />
      )}
    </div>
  );
}

// ── Report Detail Sub-component ───────────────────────────────────

function ReportDetail({
  report, onBack, onApprove, onDownload, sendEnabled,
}: {
  report: QueueReport;
  onBack: () => void;
  onApprove: (id: string) => void;
  onDownload: (id: string, type?: string) => void;
  sendEnabled: boolean;
}) {
  return (
    <div>
      <button onClick={onBack} className="text-sm text-blue-600 hover:text-blue-800 mb-4">
        &larr; Back to Queue
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left panel — metadata + downloads */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">{report.coverholder_name}</h2>
              <StatusBadge status={report.status} />
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Billing Period</p>
                <p className="mt-0.5 font-medium text-slate-800">{report.billing_period}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Generated</p>
                <p className="mt-0.5 text-slate-700">{formatDate(report.generated_at)}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Generated By</p>
                <p className="mt-0.5 text-slate-700">{report.generated_by || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase">Claims</p>
                <p className="mt-0.5 font-medium text-slate-800">{report.claim_count}</p>
              </div>
            </div>
          </div>

          {/* Stub warnings */}
          {report.stub_warnings && report.stub_warnings.length > 0 && (
            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
              <h3 className="text-sm font-semibold text-amber-800 mb-2">Stub Warnings</h3>
              <ul className="space-y-1">
                {report.stub_warnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-700 flex items-start gap-2">
                    <span className="mt-0.5 text-amber-400">!</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Downloads */}
          <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Downloads</h3>
            <div className="space-y-2">
              <button
                onClick={() => onDownload(report.report_id, 'workbook')}
                className="w-full text-left px-3 py-2 rounded bg-blue-50 border border-blue-200 text-blue-700 text-sm hover:bg-blue-100 flex items-center justify-between"
              >
                <span>Bordereaux Workbook (Excel)</span>
                <span className="text-xs text-blue-500">Download</span>
              </button>

              {report.coro_workbook_s3_key && (
                <button
                  onClick={() => onDownload(report.report_id, 'coro_workbook')}
                  className="w-full text-left px-3 py-2 rounded bg-purple-50 border border-purple-200 text-purple-700 text-sm hover:bg-purple-100 flex items-center justify-between"
                >
                  <span>CORO Workbook (Excel)</span>
                  <span className="text-xs text-purple-500">Download</span>
                </button>
              )}

              {report.invoice_s3_keys && report.invoice_s3_keys.map((key, i) => (
                <button
                  key={i}
                  onClick={() => onDownload(report.report_id, `invoice_${i}`)}
                  className="w-full text-left px-3 py-2 rounded bg-slate-50 border border-slate-200 text-slate-700 text-sm hover:bg-slate-100 flex items-center justify-between"
                >
                  <span>Invoice {i + 1} (PDF)</span>
                  <span className="text-xs text-slate-500">Download</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right panel — fee summary + approve */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Fee Summary</h3>
            <div className="text-center py-4">
              <p className="text-3xl font-bold text-blue-700 tabular-nums">{formatCurrency(report.fee_total)}</p>
              <p className="text-xs text-slate-500 mt-1">Total TCM Fees</p>
              {!TCM_FEES_ENABLED && (
                <p className="text-xs text-amber-600 mt-2">Fee data pending — T&amp;E table not yet confirmed</p>
              )}
            </div>
          </div>

          {/* Approval section */}
          <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
            <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">Approval</h3>

            {report.status === 'sent' && (
              <div className="text-center py-2">
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                  Sent {report.sent_at ? formatDate(report.sent_at) : ''}
                </span>
              </div>
            )}

            {report.status === 'approved' && (
              <div className="text-center py-2 text-sm text-slate-600">
                Approved by {report.approved_by || '—'} on {formatDate(report.approved_at)}
              </div>
            )}

            {report.status === 'failed' && report.send_error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-xs mb-3">
                {report.send_error}
              </div>
            )}

            {(report.status === 'pending' || report.status === 'failed') && (
              sendEnabled ? (
                <button
                  onClick={() => onApprove(report.report_id)}
                  className="w-full px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
                >
                  Approve &amp; Send
                </button>
              ) : (
                <div className="text-center py-2">
                  <p className="text-xs text-slate-500 mb-2">Email sending is currently disabled.</p>
                  <p className="text-xs text-slate-400">Contact Richard to enable (BDX_SEND_ENABLED=true).</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Config Editor Sub-component ───────────────────────────────────

function ConfigEditor({
  config, onSave, onCancel, loading,
}: {
  config: CoverholderConfig;
  onSave: (c: CoverholderConfig) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [form, setForm] = useState<CoverholderConfig>({ ...config });
  const [tab, setTab] = useState<'general' | 'contracts' | 'contacts'>('general');

  const update = (field: keyof CoverholderConfig, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateContact = (idx: number, field: keyof BrokerContact, value: string) => {
    const contacts = [...form.broker_contacts];
    contacts[idx] = { ...contacts[idx], [field]: value };
    setForm(prev => ({ ...prev, broker_contacts: contacts }));
  };

  const addContact = () => {
    setForm(prev => ({ ...prev, broker_contacts: [...prev.broker_contacts, { name: '', email: '' }] }));
  };

  const removeContact = (idx: number) => {
    setForm(prev => ({ ...prev, broker_contacts: prev.broker_contacts.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 max-w-2xl">
      <h2 className="text-lg font-semibold text-slate-800 mb-4">
        {config.coverholder_id ? 'Edit Coverholder' : 'Add Coverholder'}
      </h2>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 mb-6">
        {(['general', 'contracts', 'contacts'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Coverholder ID</label>
              <input type="text" value={form.coverholder_id}
                onChange={e => update('coverholder_id', e.target.value)}
                disabled={!!config.coverholder_id}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm disabled:bg-slate-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Client Code</label>
              <input type="text" value={form.client_code}
                onChange={e => update('client_code', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Coverholder Name</label>
            <input type="text" value={form.coverholder_name}
              onChange={e => update('coverholder_name', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Broker Name</label>
            <input type="text" value={form.broker_name}
              onChange={e => update('broker_name', e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Deductible Amount</label>
              <input type="number" value={form.deductible_amount}
                onChange={e => update('deductible_amount', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">TCM Fee Reserve Default</label>
              <input type="number" value={form.tcm_fee_reserve_default}
                onChange={e => update('tcm_fee_reserve_default', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
            </div>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active}
                onChange={e => update('active', e.target.checked)}
                className="rounded border-slate-300" />
              Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.coro_split}
                onChange={e => update('coro_split', e.target.checked)}
                className="rounded border-slate-300" />
              CORO Split
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.send_enabled}
                onChange={e => update('send_enabled', e.target.checked)}
                className="rounded border-slate-300" />
              Send Enabled
            </label>
          </div>
        </div>
      )}

      {tab === 'contracts' && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">Contract years are managed via DynamoDB config. Editing here is read-only for now.</p>
          {form.contract_years.map((cy, i) => (
            <div key={i} className="rounded bg-slate-50 border border-slate-100 px-4 py-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Year:</span> {cy.year_label}</div>
                <div><span className="text-slate-500">Contract:</span> {cy.contract_number}</div>
                <div><span className="text-slate-500">Effective:</span> {cy.effective_date}</div>
                <div><span className="text-slate-500">Expiry:</span> {cy.expiry_date}</div>
                <div><span className="text-slate-500">Tab:</span> {cy.tab_label}</div>
                <div>{cy.all_closed && <span className="text-amber-600 font-medium">All Claims Closed</span>}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'contacts' && (
        <div className="space-y-3">
          {form.broker_contacts.map((bc, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Name</label>
                <input type="text" value={bc.name}
                  onChange={e => updateContact(i, 'name', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-slate-500 mb-1">Email</label>
                <input type="email" value={bc.email}
                  onChange={e => updateContact(i, 'email', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </div>
              <button onClick={() => removeContact(i)}
                className="px-2 py-2 text-red-500 hover:text-red-700 text-sm">
                x
              </button>
            </div>
          ))}
          <button onClick={addContact}
            className="text-sm text-blue-600 hover:text-blue-800">
            + Add Contact
          </button>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 mt-6 pt-4 border-t border-slate-200">
        <button
          onClick={() => onSave(form)}
          disabled={loading || !form.coverholder_id || !form.coverholder_name}
          className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
        >
          {loading ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm rounded bg-slate-100 text-slate-600 hover:bg-slate-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
